import { getTime } from "@suse-o11y/date-time";
import type { Time } from "@suse-o11y/date-time/time";
import { isError } from "@suse-o11y/is-error";
import { Logger } from "@suse-o11y/logger";
import {
	type QueryClient,
	type QueryKey,
	type UseMutationOptions,
	useMutation,
	useQueryClient,
} from "@suse-o11y/query";
import { useHandler } from "@suse-o11y/use-handler";
import React from "react";

import {
	type Component,
	type ComponentType,
	type Domain,
	type Layer,
	type Monitor,
	type NodeId,
	type NodeWithOwner,
	type PreviewComponent,
	type ResolvableNameableNode,
	Type,
	isNodeOwned,
} from "~/models/dto";

import { ErrorMessage } from "../components/_notification-error";
import { modalConfirmCancellable } from "../components/react/modal/modal-utils";
import { DONT_AUTO_DISMISS, notification } from "../components/react/notification/Notification";
import { handleLockNode } from "../components/react/setting-node/SettingNodeUtils";
import { request } from "../generic/request";

import { type InvalidateFunction, type UseQueryOptions, type UseQueryResult, useQuery } from "./react-query";

type QueryNode = ResolvableNameableNode;

export type QueryNodeType = QueryNode["_type"];

export type GetQueryNodeByType<TType extends QueryNodeType> = Extract<QueryNode, { _type: TType }>;

export const useQueryNodesList = <TNodeType extends QueryNodeType, TResult = Array<GetQueryNodeByType<TNodeType>>>(
	nodeType: TNodeType,
	options?: UseQueryOptions<Array<GetQueryNodeByType<TNodeType>>, Error, TResult>,
): UseQueryResult<TResult, Error> => {
	const { queryKey, queryFn } = queryQueryNodesList(nodeType, options);
	return useQuery(queryKey, queryFn, options);
};

export const queryQueryNodesList = <TNodeType extends QueryNodeType>(
	nodeType: TNodeType,
	{ timestamp }: { timestamp?: null | Time } = {},
): {
	queryKey: QueryKey;
	queryFn: () => Promise<Array<GetQueryNodeByType<TNodeType>>>;
} => ({
	queryKey: ["node", nodeType],
	queryFn: () =>
		request<Array<GetQueryNodeByType<TNodeType>>>(`/api/node/${nodeType}`, {
			query: {
				queryTime: getTime(timestamp),
				timeoutSeconds: 15,
				fullNode: true,
			},
		}),
});

export type Identifier = string;

export const useQueryNodeById = <TNodeType extends QueryNodeType, TResult = GetQueryNodeByType<TNodeType>>(
	nodeType: TNodeType,
	nodeId: null | undefined | NodeId | Identifier,
	{ enabled = true, timestamp, ...options }: UseQueryOptions<GetQueryNodeByType<TNodeType>, Error, TResult> = {},
): UseQueryResult<TResult, Error> => {
	return useQuery(
		["node", nodeType, nodeId],
		() =>
			request<GetQueryNodeByType<TNodeType>>(`/api/node/${nodeType}/${nodeId!}`, {
				query: {
					queryTime: getTime(timestamp),
					timeoutSeconds: 15,
					fullNode: true,
				},
			}),
		{
			...options,
			timestamp,
			// the query fetches node only when the id is defined
			enabled: enabled && nodeId != null,
		},
	);
};

export const useResetNodeByIdQuery = (): InvalidateFunction<
	[nodeType: QueryNodeType, nodeId: null | undefined | NodeId | Identifier]
> => {
	const queryClient = useQueryClient();

	return useHandler((nodeType, nodeId) => {
		if (nodeId == null) {
			return Promise.resolve();
		}

		return queryClient.resetQueries([{}, "node", nodeType, nodeId]);
	});
};

export interface SearchResultData<T> {
	items: Array<T>;
	hasMoreItems: boolean;
}

interface SearchOptions {
	queryString: string;
	limit: number;
	timestamp?: number;
}

type SearchNode = Component | ComponentType | Domain | Layer | Monitor | PreviewComponent;

export type SearchNodeType = SearchNode["_type"];

export type SearchNodeByType<TType extends SearchNodeType> = Extract<SearchNode, { _type: TType }>;

/** Backend response doesn't contain hasMoreItems flag,
 * do limit+1 request to slice response and do hasMoreItems switch accordingly */
export const useQuerySimpleSearch = <
	TNodeType extends SearchNodeType,
	TResult = SearchResultData<SearchNodeByType<TNodeType>>,
>(
	type: TNodeType,
	{ queryString, limit, timestamp }: SearchOptions,
	options?: UseQueryOptions<SearchResultData<SearchNodeByType<TNodeType>>, Error, TResult>,
): UseQueryResult<TResult, Error> => {
	const params = { q: queryString, limit: limit + 1 };

	return useQuery(
		["search", type, params],
		() =>
			request<Array<SearchNodeByType<TNodeType>>>("/api/search", {
				query: {
					...params,
					_type: type,
					match: "PrefixMatch",
					timestampMs: timestamp,
				},
			}).then((data) => ({
				items: data.slice(0, limit),
				hasMoreItems: data.length > limit,
			})),
		{
			...options,
			timestamp,
			onError: (error: Error) => {
				Logger.namespace(`Search API ${type}`).error(error);
				options?.onError?.(error);
			},
		},
	);
};

export const useInvalidateNodesListQuery = (nodeType: QueryNodeType): InvalidateFunction => {
	const queryClient = useQueryClient();

	return useHandler(() => invalidateNodesListQuery(queryClient, nodeType));
};

const invalidateNodesListQuery = (queryClient: QueryClient, nodeType: QueryNodeType): Promise<void> =>
	queryClient.invalidateQueries([{}, "node", nodeType]);

export const useExportNodeQuery = <TResult = string>(
	nodeId: number | NodeId,
	options?: UseQueryOptions<string, Error, TResult>,
): UseQueryResult<TResult, Error> =>
	useQuery(
		["nodeExport", nodeId],
		() =>
			request("/api/export", {
				accept: "*",
				method: "POST",
				body: JSON.stringify({ nodesWithIds: [nodeId] }),
			}),
		options,
	);

export const mutateNode = <TNodeType extends QueryNodeType>(nodeType: TNodeType) =>
	({
		mutationKey: ["node", nodeType],
		mutationFn: async (node: GetQueryNodeByType<TNodeType>) => {
			const [responseNode] = await request<[GetQueryNodeByType<TNodeType>]>("/api/node/_", {
				method: "POST",
				query: {
					timeoutSeconds: 15,
				},
				body: [node],
			});
			return responseNode;
		},
	}) as const satisfies UseMutationOptions<GetQueryNodeByType<TNodeType>, Error, GetQueryNodeByType<TNodeType>>;

export const mutateUnlockNodeById = <TNodeType extends QueryNodeType>(nodeType: TNodeType) =>
	({
		mutationKey: ["node", "unlock", nodeType],
		mutationFn: (node: GetQueryNodeByType<TNodeType>) =>
			request<GetQueryNodeByType<TNodeType>>(`/api/node/${nodeType}/${node.id}/unlock`, {
				method: "POST",
				query: { timeoutSeconds: 15 },
			}),
	}) as const satisfies UseMutationOptions<GetQueryNodeByType<TNodeType>, Error, GetQueryNodeByType<TNodeType>>;

const mutateDeleteNodeQuery = <TNodeType extends QueryNodeType>(nodeType: TNodeType) =>
	({
		mutationKey: ["node", "delete", nodeType],
		mutationFn: (node) =>
			request(`/api/node/_/${node.id}`, {
				method: "DELETE",
				query: {
					timeoutSeconds: 15,
				},
			}),
	}) as const satisfies UseMutationOptions<void, Error, GetQueryNodeByType<TNodeType>>;

export const invalidateStackPackNodes = (queryClient: QueryClient): Promise<void> =>
	Promise.all([
		invalidateNodesListQuery(queryClient, Type.ViewType),
		invalidateNodesListQuery(queryClient, Type.MainMenuGroup),
	]).then(() => undefined);

type ActionType = "save" | "delete";
interface UsePersistNodeFlowResult<TNodeType extends QueryNodeType> {
	mutate: (
		action: ActionType,
		node: GetQueryNodeByType<TNodeType>,
		confirmation?: (node: string) => string,
	) => Promise<void>;
	isLoading: boolean;
}

export const usePersistNodeFlow = <TNodeType extends Extract<QueryNodeType, NodeWithOwner["_type"]>>(
	nodeType: TNodeType,
	friendlyName: string,
	onSuccess?: (node: GetQueryNodeByType<TNodeType>) => void,
	onSaveError?: (error: Error, node: GetQueryNodeByType<TNodeType>) => void,
): UsePersistNodeFlowResult<TNodeType> => {
	const queryClient = useQueryClient();

	const getUserFriendlyNodeName = (nodeName: string) => `${friendlyName} "${nodeName}"`;

	const invalidateViewCaches = () => {
		if (nodeType === Type.QueryView) {
			queryClient.invalidateQueries({
				predicate: (query) => query.queryKey.includes("queryViews"),
			});
		}
	};

	const { mutateAsync: saveNode, isLoading: isSaving } = useMutation<
		GetQueryNodeByType<TNodeType>,
		Error,
		GetQueryNodeByType<TNodeType>
	>({
		...mutateNode(nodeType),
		onSuccess: (savedNode) => {
			invalidateViewCaches();
			if (onSuccess == null) {
				notification.success({
					message: `The ${getUserFriendlyNodeName(savedNode.name)} has been successfully saved.`,
				});
			} else {
				onSuccess(savedNode);
			}
		},
		onError: (error, node) => {
			if (onSaveError == null) {
				notification.error({
					duration: DONT_AUTO_DISMISS,
					message: (
						<ErrorMessage error={error}>
							Something went wrong while trying to save {getUserFriendlyNodeName(node.name)}.
						</ErrorMessage>
					),
				});
			} else {
				onSaveError(error, node);
			}
		},
	});

	const { mutateAsync: deleteNode, isLoading: isDeleting } = useMutation<
		void,
		Error,
		GetQueryNodeByType<TNodeType>,
		{ name: string }
	>({
		...mutateDeleteNodeQuery(nodeType),
		onSuccess: (_, deletedNode) => {
			invalidateViewCaches();
			notification.success({
				message: `The ${getUserFriendlyNodeName(deletedNode.name)} has been successfully deleted.`,
			});
		},
		onError: (error, node) => {
			notification.error({
				duration: DONT_AUTO_DISMISS,
				message: (
					<ErrorMessage error={error}>
						Something went wrong while trying to delete the {getUserFriendlyNodeName(node.name)}.
					</ErrorMessage>
				),
			});
		},
	});

	const handlePersist = async (action: "save" | "delete", node: GetQueryNodeByType<TNodeType>) => {
		if (action === "delete") {
			await deleteNode(node);
			return;
		}
		await saveNode(node);
	};

	const { mutateAsync: unlockNode, isLoading: isUnlocking } = useMutation<
		GetQueryNodeByType<TNodeType>,
		Error,
		GetQueryNodeByType<TNodeType>
	>({
		...mutateUnlockNodeById(nodeType),
		onError: (error, node) => {
			notification.error({
				duration: DONT_AUTO_DISMISS,
				message: (
					<ErrorMessage error={error}>
						Something went wrong while trying to unlock the {getUserFriendlyNodeName(node.name)}.
					</ErrorMessage>
				),
			});
		},
	});

	const onNodeMutate = useHandler(
		async (action: "save" | "delete", node: GetQueryNodeByType<TNodeType>, confirmation?: (node: string) => string) => {
			if (isNodeOwned(node)) {
				const unlockedNode = await handleLockNode(node);
				if (!isError(unlockedNode)) {
					await unlockNode(node);
					await handlePersist(action, unlockedNode);
				}
				return;
			}

			if (confirmation != null) {
				const modalResult = await modalConfirmCancellable(confirmation(getUserFriendlyNodeName(node.name)));

				if (isError(modalResult) || !modalResult) {
					return;
				}
			}

			return handlePersist(action, node);
		},
	);

	return {
		mutate: onNodeMutate,
		isLoading: isSaving || isDeleting || isUnlocking,
	};
};
